import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ENVIRONMENT,
  GetLoansByEmailResponse,
  LoanFlag,
  LoanLookupResponse,
  LoanMessagesResponse,
  LoanRequestItem,
  LoanResponse,
  LoanSummary,
  LossMitigationData,
  OtherLoan,
  QrpcTemplate
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoanService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);

  private get loansBaseUrl(): string {
    return this.env.appSettings.ServicingLINCPortalLoans || '';
  }

  private get melloServBaseUrl(): string {
    return this.env.appSettings.melloServDomainUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async getLoans(loanRequests: LoanRequestItem[]): Promise<LoanResponse[]> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/GetLoans`;

    return firstValueFrom(this.http.post<LoanResponse[]>(url, loanRequests));
  }

  async getLoanMessages(loanRequests: LoanRequestItem[], client = 1): Promise<LoanMessagesResponse> {
    const url = `${this.melloServBaseUrl}/${this.apiNamespace}/MelloServDomain/DynamicMessages/LoanMessages?client=${client}`;

    return firstValueFrom(this.http.post<LoanMessagesResponse>(url, loanRequests));
  }

  async getLoanFlags(loanRequests: LoanRequestItem[]): Promise<LoanFlag[]> {
    const url = `${this.melloServBaseUrl}/${this.apiNamespace}/MelloServDomain/Flags/LoanFlags`;

    return firstValueFrom(this.http.post<LoanFlag[]>(url, loanRequests));
  }

  async getLoan(loanNumber: string, encryptionValue: string): Promise<LoanResponse> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/GetLoan/${loanNumber}/${encryptionValue}`;

    return firstValueFrom(this.http.get<LoanResponse>(url));
  }

  async getLoanSummary(loanNumber: string, encryptionValue: string): Promise<LoanSummary> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/GetLoanSummary/${loanNumber}/${encryptionValue}`;

    return firstValueFrom(this.http.get<LoanSummary>(url));
  }

  async getOtherLoans(loanNumber: string): Promise<OtherLoan[]> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/OtherLoans/${loanNumber}`;

    return firstValueFrom(this.http.get<OtherLoan[]>(url));
  }

  async getQrpcTemplate(loanNumber: string, encryptionValue: string): Promise<QrpcTemplate> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/GetQrpcTemplate/${loanNumber}/${encryptionValue}`;

    return firstValueFrom(this.http.get<QrpcTemplate>(url));
  }

  async getLossMitigationData(loanNumber: string, encryptionValue: string): Promise<LossMitigationData> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/Getlossmitigationworkstationdata/${loanNumber}/${encryptionValue}`;

    return firstValueFrom(this.http.get<LossMitigationData>(url));
  }

  async loanLookup(email: string): Promise<LoanLookupResponse> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/LoanLookup`;
    return firstValueFrom(this.http.post<LoanLookupResponse>(url, { email }));
  }

  async getLoansByEmail(email: string): Promise<GetLoansByEmailResponse> {
    const url = `${this.loansBaseUrl}/${this.apiNamespace}/ServicingLINCPortal/Loan/GetLoansByEmail`;
    return firstValueFrom(this.http.post<GetLoansByEmailResponse>(url, { email }));
  }
}
